import { api } from '../lib/api';

// Lightweight chat service wrapper around backend chat endpoints
const chatService = {
  async getChats(userId) {
    const res = await api.get('/chats', { params: { user_id: userId } });
    return res?.data?.data?.chats || res?.data?.chats || res?.data || [];
  },
  
  async getChatById(chatId) {
    const res = await api.get(`/chats/${chatId}`);
    return res?.data?.data?.chat || res?.data?.chat || res?.data || null;
  },
  
  async createChatWithCreative(creativeUserId) {
    const res = await api.post('/chats', { creative_user_id: creativeUserId });
    return res?.data?.data?.chat || res?.data?.chat || res?.data;
  },
  
  async createChatWithClient(clientUserId) {
    const res = await api.post('/chats', { client_user_id: clientUserId });
    return res?.data?.data?.chat || res?.data?.chat || res?.data;
  },
  
  async getMessages(chatId, { limit = 50 } = {}) {
    try {
      const res = await api.get(`/chats/${chatId}/messages`, { params: { limit } });
      return res?.data?.data?.messages || res?.data?.messages || res?.data || [];
    } catch (e) {
      // Fallback for legacy mount
      try {
        const res2 = await api.get(`/messages/chats/${chatId}/messages`, { params: { limit } });
        return res2?.data?.data?.messages || res2?.data?.messages || res2?.data || [];
      } catch {
        throw e;
      }
    }
  },
  
  async sendMessage(chatId, content, attachment_url = null) {
    const payload = { content };
    if (attachment_url) {
      payload.attachment_url = attachment_url;
    }
    
    try {
      const res = await api.post(`/chats/${chatId}/messages`, payload);
      return res?.data?.data || res?.data;
    } catch (e) {
      // Fallback for legacy mount
      try {
        const res2 = await api.post(`/messages/chats/${chatId}/messages`, payload);
        return res2?.data?.data || res2?.data;
      } catch {
        throw e;
      }
    }
  },
  
  async sendMessageWithAttachments(chatId, content, attachments = []) {
    const payload = {};
    
    // Only include content if it has actual text
    if (content && content.trim()) {
      payload.content = content.trim();
    }
    
    // Only include attachments if there are any
    if (Array.isArray(attachments) && attachments.length > 0) {
      // Ensure each attachment has the required fields and clean structure
      payload.attachments = attachments.map(a => ({
        url: String(a.url || ''),
        type: String(a.type || 'file'),
        name: String(a.name || a.url?.split('/').pop() || 'attachment')
      })).filter(a => a.url); // Remove any with empty URLs
    }
    
    // Don't send empty payload
    if (!payload.content && (!payload.attachments || payload.attachments.length === 0)) {
      throw new Error('Message must have content or attachments');
    }
    
    try {
      const res = await api.post(`/chats/${chatId}/messages`, payload);
      return res?.data?.data || res?.data;
    } catch (e) {
      // Fallback for legacy mount
      try {
        const res2 = await api.post(`/messages/chats/${chatId}/messages`, payload);
        return res2?.data?.data || res2?.data;
      } catch {
        throw e;
      }
    }
  },
  
  async markRead() {
    return { ok: true };
  },
  
  async markChatRead(chatId) {
    try {
      const res = await api.post(`/chats/${chatId}/read`);
      return res?.data?.data || res?.data;
    } catch {
      return { ok: true };
    }
  },
  
  async getUnreadCount() {
    try {
      const res = await api.get('/chats/unread-count');
      const total = res?.data?.data?.total ?? res?.data?.total ?? 0;
      return Number(total) || 0;
    } catch {
      return 0;
    }
  },
  
  async findOrCreateChat(userAId, userBId) {
    const list = await this.getChats(userAId);
    const existing = (Array.isArray(list) ? list : []).find((c) => {
      const ids = [
        c?.client_user_id,
        c?.creative_user_id,
        c?.client_id,
        c?.creative_id,
        c?.buyer_id,
        c?.talent_id,
        c?.user1_id,
        c?.user2_id,
        c?.participant1_id,
        c?.participant2_id,
      ].filter(Boolean).map(String);
      return ids.includes(String(userAId)) && ids.includes(String(userBId));
    });
    if (existing) return existing;
    return this.createChatWithCreative(userBId);
  },
  
  async listParticipants(chatId) {
    const res = await api.get(`/chats/${chatId}/participants`);
    const d = res?.data || res;
    return d?.data?.participants || d?.participants || d?.data || [];
  },
  
  async addParticipant(chatId, userId, role = 'member') {
    const res = await api.post(`/chats/${chatId}/participants`, { participant_id: userId, role });
    const d = res?.data || res;
    return d?.data?.participant || d?.participant || d?.data || d;
  },
  
  async removeParticipant(chatId, userId) {
    const res = await api.delete(`/chats/${chatId}/participants/${userId}`);
    return res?.data || res;
  },
  
  async archiveChat(chatId) {
    const res = await api.post(`/chats/${chatId}/archive`);
    return res?.data?.data || res?.data || res;
  },
  
  async deleteChat(chatId) {
    const res = await api.delete(`/chats/${chatId}`);
    return res?.data || res;
  },
};

export default chatService;
